import React, { useCallback } from 'react';

import Tag from 'antd/es/tag';

import { ResourceType } from 'services/resources';
import useResource from 'hooks/useResource';
import useAdminResource from '../hooks/useAdminResource';

import AdminLayout from 'layouts/Admin.layout';
import FormModal from 'components/FormModal';
import Table from 'components/Table';
import Button from 'components/Button';
import Search from 'components/Search';

import UserForm from '../components/forms/User.form';
import { EditAction, DeleteAction } from '../components/TableActions';

import { mapLocationsToOptions } from 'helpers/mapToOptions';

const UsersPage = () => {
  const locations = useResource(ResourceType.locations, { prefetch: true });
  const users = useAdminResource({
    resource: ResourceType.users,
    resourceName: 'user',
    searchFields: [
      'user_id',
      'full_name',
      'location_name',
      'email',
    ],
  });

  const onAction = useCallback((actionType, item) => {
    switch (actionType) {
      case EditAction.actionType:
        users.onEdit(item);
        break

      case DeleteAction.actionType:
        users.onDelete(item.user_id);
        break;

      default: // make eslint happy :)
    }
  }, [users]);

  return (
    <AdminLayout
      title="Users"
      icon="user"
      extra={[
        <Search
          placeholder={users.strings.searchPlaceholder}
          value={users.searchVal}
          onChange={value => users.search(value)}
        />,
        <Button
          type="primary"
          icon="plus"
          onClick={() => {
            users.onCreate({ is_active: 1 });
          }}
        >
          {users.strings.addButton}
        </Button>
      ]}
    >
      <FormModal
        title={users.strings.formTitle}
        form={users.form}
        submitText={users.strings.formSubmitButton}
        onClose={() => users.form.reset()}
        width="316px"
      >
        <UserForm form={users.form} locationOptions={mapLocationsToOptions(locations.state.list)} />
      </FormModal>
      <Table
        loading={users.isLoading || locations.state.isLoading}
        dataSource={users.list}
        actions={[
          EditAction,
        ]}
        onAction={onAction}
        actionsProps={{
          resource: 'user',
        }}
        rowKey="user_id"
      >
        <Table.Column title="ID" dataIndex="user_id" />
        <Table.Column title="Name" dataIndex="full_name" />
        <Table.Column title="Email" dataIndex="email" />
        <Table.Column title="Location" dataIndex="location_id" render={locationId => {
          const location = locations.state.list.find(l => l.location_id === locationId);
          if (!location) return '-';
          else return location.location_name;
        }} />
        <Table.Column title="Last Activity" dataIndex="last_activity" render={last_activity => {
          return last_activity ? new Date(last_activity).toLocaleDateString() : '-';
        }} />
        <Table.Column title="Status" dataIndex="is_active" render={is_active => {
          const isActive = is_active === 1;
          return isActive ? (
            <Tag color='green'>Active</Tag>
          ) : (
            <Tag color='red'>Inactive</Tag>
          );
        }} />
      </Table>
    </AdminLayout>
  );
};

export default UsersPage;